// ✅ src/services/problemService.js

import { logger } from "../utils/logger";
import { db } from "../firebase";
import {
  collection,
  doc,
  runTransaction,
  serverTimestamp,
  onSnapshot,
  query,
  where,
  orderBy,
  addDoc,
  deleteDoc, // ✅ from Firestore
  updateDoc,
  getDoc,
  getDocs,
} from "firebase/firestore"; // ✅ correct import source
import { BRAND } from "../config/brand";
import {
  INCIDENT_EVENTS,
  recordIncidentEvent,
} from "./incidentEventService";

/**
 * ✅ Assign a problem to a helper
 */
export const assignProblem = async (
  problemId,
  helperId,
  currentHelperName,
  currentHelperEmail
) => {
  const problemDocRef = doc(db, "problems", problemId);
  const helperRef = doc(db, "users", helperId);

  try {
    let clientEmail = null;
    let problemTitle = null;
    let finalHelperName = currentHelperName || "Unknown Helper";
    let finalHelperEmail = currentHelperEmail || BRAND.noReplyEmail;

    await runTransaction(db, async (transaction) => {
      const problemDoc = await transaction.get(problemDocRef);
      if (!problemDoc.exists()) throw new Error("Problem does not exist");
      if (problemDoc.data().status !== "open")
        throw new Error("Problem is not available");

      const helperDoc = await transaction.get(helperRef);
      if (helperDoc.exists()) {
        const helperData = helperDoc.data();
        finalHelperName =
          currentHelperName || helperData.displayName || finalHelperName;
        finalHelperEmail =
          currentHelperEmail || helperData.email || finalHelperEmail;
      }

      transaction.update(problemDocRef, {
        status: "assigned",
        helperId,
        helperName: finalHelperName,
        helperEmail: finalHelperEmail,
        assignedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const problemData = problemDoc.data();
      clientEmail = problemData.clientEmail;
      problemTitle = problemData.title;
    });

    const workspaceUrl = `${window.location.origin}/workspace/${problemId}`;

    // 🔹 Email notifications are now handled by Firestore Cloud Functions
    logger.log("📧 Email notification will be sent via Firestore trigger");

    return { success: true, clientEmail, problemTitle, workspaceUrl };
  } catch (error) {
    console.error("❌ Error assigning problem:", error);
    return { success: false, error: error.message };
  }
};

/**
 * ✅ takeJob – an expert claims an open incident.
 *
 * The claim runs in a transaction guarded on status, because two experts can
 * have the same incident open in their queue and click Accept at the same
 * moment. Without the guard the second write silently reassigns the incident
 * and the first expert keeps working on a job they no longer own.
 */
export async function takeJob(problemId, currentUser) {
  const helperName = currentUser.displayName || "Unnamed Helper";
  const helperEmail = currentUser.email || BRAND.noReplyEmail;

  try {
    const pRef = doc(db, "problems", problemId);

    let clientEmail = null;
    let clientId = null;
    let problemTitle = null;

    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(pRef);
      if (!snap.exists()) throw new Error("Problem not found");

      const problemData = snap.data();
      if (problemData.status !== "open" || problemData.helperId) {
        throw new Error(
          "This incident has already been claimed by another expert."
        );
      }

      clientEmail = problemData.clientEmail || null;
      clientId = problemData.clientId || null;
      problemTitle = problemData.title || null;

      transaction.update(pRef, {
        helperId: currentUser.uid,
        helperName,
        helperEmail,
        status: "assigned",
        assignedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });

    await recordIncidentEvent(problemId, {
      type: INCIDENT_EVENTS.EXPERT_ASSIGNED,
      actor: { id: currentUser.uid, name: helperName, role: "expert" },
      summary: `${helperName} claimed this incident`,
      metadata: { assignedVia: "queue-claim" },
    });

    // Add system message
    await addDoc(collection(db, "problems", problemId, "messages"), {
      type: "system",
      text: `${helperName} accepted the job. You're ready to collaborate.`,
      senderId: currentUser.uid,
      createdAt: serverTimestamp(),
    });

    // Add notification for the client
    await addDoc(collection(db, "notifications"), {
      userId: clientId,
      type: "job-assigned",
      problemId,
      helperId: currentUser.uid,
      message: `${helperName} accepted your job "${problemTitle}". They can start right away.`,
      createdAt: serverTimestamp(),
      read: false,
    });

    const workspaceUrl = `${window.location.origin}/workspace/${problemId}`;

    return {
      success: true,
      clientEmail,
      problemTitle,
      helperName,
      helperEmail,
      workspaceUrl,
    };
  } catch (error) {
    console.error("❌ Error taking job:", error);
    return { success: false, error: error.message };
  }
}

/**
 * ✅ Subscribe to live list of open problems
 */
export const subscribeOpenProblems = (callback) => {
  const q = query(
    collection(db, "problems"),
    where("status", "==", "open"),
    orderBy("createdAt", "desc")
  );

  return onSnapshot(q, (snapshot) => {
    const problems = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(problems);
  });
};

/**
 * ✅ Subscribe to problems assigned to the logged-in helper
 */
export const subscribeMyProblems = (helperId, callback) => {
  if (!helperId) return () => {};

  const q = query(
    collection(db, "problems"),
    where("helperId", "==", helperId),
    orderBy("assignedAt", "desc")
  );

  return onSnapshot(q, (snapshot) => {
    const problems = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(problems);
  });
};

/**
 * ✅ Invite a helper to a problem (client -> helper)
 */
export const inviteHelperToProblem = async (problemId, clientId, helperId) => {
  try {
    // Ensure helperId is the correct identifier (uid from users collection)
    if (!helperId) {
      return { success: false, error: "Helper ID is required" };
    }
    
    logger.log("Creating invitation:", { problemId, clientId, helperId });
    
    const ref = collection(db, "invitations");
    const docRef = await addDoc(ref, {
      problemId,
      clientId,
      helperId: String(helperId), // Ensure it's a string for consistency
      status: "pending",
      requestedBy: "client", // Indicates this was requested by client
      createdAt: serverTimestamp(),
    });
    
    logger.log("✅ Invitation created:", docRef.id);
    return { success: true, id: docRef.id };
  } catch (err) {
    console.error("❌ Error inviting helper:", err);
    return { success: false, error: err.message };
  }
};

/**
 * ✅ Request to work on a problem (helper -> client)
 */
export const requestToWorkOnProblem = async (problemId, helperId, clientId) => {
  try {
    if (!helperId || !clientId || !problemId) {
      return { success: false, error: "Helper ID, Client ID, and Problem ID are required" };
    }
    
    // Ensure all IDs are strings for consistency
    const helperIdStr = String(helperId);
    const clientIdStr = String(clientId);
    const problemIdStr = String(problemId);
    
    logger.log("Creating helper request:", { 
      problemId: problemIdStr, 
      helperId: helperIdStr, 
      clientId: clientIdStr 
    });
    
    // 🔍 Check if invitation already exists
    const invitationsRef = collection(db, "invitations");
    const existingQuery = query(
      invitationsRef,
      where("problemId", "==", problemIdStr),
      where("helperId", "==", helperIdStr),
      where("clientId", "==", clientIdStr)
    );
    
    try {
      const existingSnap = await getDocs(existingQuery);
      if (!existingSnap.empty) {
        return { success: false, error: "You have already sent a request for this problem" };
      }
    } catch (queryErr) {
      console.warn("Could not check for existing invitations (may not have permission):", queryErr);
      // Continue anyway - the create will fail if duplicate
    }
    
    // 🔍 Fetch problem + helper info for notification message
    let problemTitle = "your problem";
    let helperName = "A helper";

    try {
      const problemSnap = await getDoc(doc(db, "problems", problemIdStr));
      if (problemSnap.exists()) {
        const data = problemSnap.data();
        if (data?.title) problemTitle = data.title;
      }
    } catch (e) {
      console.warn("Could not fetch problem for helper request notification:", e);
    }

    try {
      const helperSnap = await getDoc(doc(db, "users", helperIdStr));
      if (helperSnap.exists()) {
        const data = helperSnap.data();
        if (data?.name) {
          helperName = data.name;
        } else if (data?.displayName) {
          helperName = data.displayName;
        }
      }
    } catch (e) {
      console.warn("Could not fetch helper user for helper request notification:", e);
    }

    // ✅ Create invitation document
    const inviteRef = await addDoc(invitationsRef, {
      problemId: problemIdStr,
      clientId: clientIdStr,
      helperId: helperIdStr,
      status: "pending",
      requestedBy: "helper", // Indicates this was requested by helper, not client
      createdAt: serverTimestamp(),
    });
    
    logger.log("✅ Helper request created:", inviteRef.id);

    // ✅ Create notification for the client so they see the request
    try {
      const notifMessage = `${helperName} requested to solve your problem "${problemTitle}". Accept or decline?`;

      await addDoc(collection(db, "notifications"), {
        userId: clientIdStr,
        type: "helper-request",
        problemId: problemIdStr,
        helperId: helperIdStr,
        helperName: helperName,
        message: notifMessage,
        createdAt: serverTimestamp(),
        read: false,
      });
    } catch (notifErr) {
      console.warn("Failed to create notification for helper request:", notifErr);
      // Don't fail the whole operation if notification fails
    }

    return { success: true, id: inviteRef.id };
  } catch (err) {
    console.error("❌ Error creating helper request:", err);
    // Provide more detailed error message
    if (err.code === 'permission-denied') {
      return { success: false, error: "Permission denied. Please ensure you are logged in and have the correct permissions." };
    }
    return { success: false, error: err.message || "Failed to send request" };
  }
};

/**
 * ✅ Accept a helper request (client accepts helper's request to work on problem)
 */
export const acceptHelperRequest = async (problemId, helperId, clientId) => {
  try {
    const problemDocRef = doc(db, "problems", problemId);
    const helperRef = doc(db, "users", helperId);

    let helperName = "Helper";
    let helperEmail = BRAND.noReplyEmail;

    await runTransaction(db, async (transaction) => {
      const problemDoc = await transaction.get(problemDocRef);
      if (!problemDoc.exists()) throw new Error("Problem does not exist");
      
      const problemData = problemDoc.data();
      // Check if problem belongs to this client
      if (problemData.clientId !== clientId) {
        throw new Error("You don't have permission to assign this problem");
      }
      
      // Check if problem is still open
      if (problemData.status !== "open") {
        throw new Error("Problem is no longer available");
      }

      // Get helper info
      const helperDoc = await transaction.get(helperRef);
      if (helperDoc.exists()) {
        const helperData = helperDoc.data();
        helperName = helperData.name || helperData.displayName || helperName;
        helperEmail = helperData.email || helperEmail;
      }

      // Assign problem to helper — do NOT overwrite paymentStatus so that
      // paid problems retain paymentStatus: "paid" after acceptance.
      transaction.update(problemDocRef, {
        status: "assigned",
        helperId: helperId,
        helperName: helperName,
        helperEmail: helperEmail,
        assignedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });

    await recordIncidentEvent(problemId, {
      type: INCIDENT_EVENTS.EXPERT_ASSIGNED,
      actor: { id: clientId, name: "Customer", role: "client" },
      summary: `${helperName} was assigned by the customer`,
      metadata: { assignedVia: "customer-accepted-request", helperId },
    });

    // Update invitation status to accepted
    try {
      const invitationsRef = collection(db, "invitations");
      const inviteQuery = query(
        invitationsRef,
        where("problemId", "==", problemId),
        where("helperId", "==", helperId),
        where("clientId", "==", clientId),
        where("status", "==", "pending")
      );
      const inviteSnap = await getDocs(inviteQuery);
      
      if (!inviteSnap.empty) {
        const inviteDoc = inviteSnap.docs[0];
        await updateDoc(doc(db, "invitations", inviteDoc.id), {
          status: "accepted",
          acceptedAt: serverTimestamp(),
        });
      }
    } catch (inviteErr) {
      console.warn("Could not update invitation status:", inviteErr);
      // Don't fail if invitation update fails
    }

    // Add system message to workspace
    try {
      await addDoc(collection(db, "problems", problemId, "messages"), {
        type: "system",
        text: `${helperName} has been assigned to this problem. You can now start collaborating.`,
        senderId: clientId,
        createdAt: serverTimestamp(),
      });
    } catch (msgErr) {
      console.warn("Could not add system message:", msgErr);
      // Don't fail if message creation fails
    }

    logger.log("✅ Helper request accepted, problem assigned:", problemId);
    return { success: true, helperName, helperEmail };
  } catch (error) {
    console.error("❌ Error accepting helper request:", error);
    return { success: false, error: error.message };
  }
};

/**
 * ✅ Decline a helper request
 */
export const declineHelperRequest = async (problemId, helperId, clientId) => {
  try {
    const invitationsRef = collection(db, "invitations");
    const inviteQuery = query(
      invitationsRef,
      where("problemId", "==", problemId),
      where("helperId", "==", helperId),
      where("clientId", "==", clientId),
      where("status", "==", "pending")
    );
    const inviteSnap = await getDocs(inviteQuery);
    
    if (!inviteSnap.empty) {
      const inviteDoc = inviteSnap.docs[0];
      await updateDoc(doc(db, "invitations", inviteDoc.id), {
        status: "declined",
        declinedAt: serverTimestamp(),
      });
      logger.log("✅ Helper request declined");
      return { success: true };
    } else {
      return { success: false, error: "Invitation not found" };
    }
  } catch (error) {
    console.error("❌ Error declining helper request:", error);
    return { success: false, error: error.message };
  }
};

/**
 * ✅ Delete a problem by ID (for client dashboard)
 */
export const deleteProblem = async (problemId) => {
  try {
    const ref = doc(db, "problems", problemId);
    await deleteDoc(ref);
    logger.log("🗑️ Problem deleted successfully:", problemId);
    return { success: true };
  } catch (error) {
    console.error("❌ Error deleting problem:", error);
    return { success: false, error: error.message };
  }
};

/**
 * ✅ Create a new problem (for clients)
 */
export const createProblem = async (problemData) => {
  try {
    const problemsRef = collection(db, "problems");
    const newProblem = {
      ...problemData,
      status: "open",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Record payment timestamp server-side when the problem was paid for.
      ...(problemData.paymentIntentId ? { paidAt: serverTimestamp() } : {}),
    };

    const docRef = await addDoc(problemsRef, newProblem);
    logger.log("🆕 Problem created with ID:", docRef.id);

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("❌ Error creating problem:", error);
    return { success: false, error: error.message };
  }
};
